import {
  buildCibliErrorFromAxios,
  createApiClient,
  CibliError,
  DEFAULT_LANGUAGE,
  isAbortError,
} from '@cibli/shared';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { t } from 'i18next';
import { toast } from 'sonner';

export type ApiOptions = {
  showErrorToast?: boolean;
};

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

let currentLanguage: string = DEFAULT_LANGUAGE;

export const setApiLanguage = (language: string) => {
  currentLanguage = language;
};

export const getCurrentLanguage = (): string => currentLanguage;

const showErrorToast = (message: string): void => {
  toast.error(message, {
    unstyled: true,
    classNames: {
      error: 'flex flex-row items-center gap-2 bg-red-500 text-white p-4 rounded-lg',
    },
  });
};

function handleApiError(error: unknown, showToast: boolean): never {
  if (isAbortError(error)) {
    throw error;
  }

  const cibliError =
    axios.isAxiosError(error) && error.response?.data
      ? buildCibliErrorFromAxios(error, t)
      : error instanceof CibliError
        ? error
        : new CibliError(t('errors.internal_error'));

  Sentry.captureException(error, {
    extra: {
      message: cibliError.response?.data?.message,
      code: cibliError.response?.data?.code,
    },
  });

  const errorCode = cibliError.response.data.code;
  const shouldShowToast =
    showToast && errorCode !== 'account_archived' && errorCode !== 'phone_archived';

  if (shouldShowToast) {
    showErrorToast(cibliError.response.data.message);
  }

  console.error(cibliError);
  throw cibliError;
}

const api = createApiClient({
  baseURL: API_URL,
  withCredentials: true, // Send session cookie so /users/me is authenticated after OTP
  getRequestHeaders: () => ({
    'x-language': currentLanguage,
    'x-app-source': 'interview',
  }),
  handleError: handleApiError,
});

export default api;
